import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlotSettingsSectionProps } from './types';

/**
 * PlotSettingsSection sub-component
 * Displays plot configuration options (num_sbpl, x_sbpl, y_sbpl_fmt, y_sbpl_set)
 */
export const PlotSettingsSection: React.FC<PlotSettingsSectionProps> = memo(({
  plotConfig,
  onNumSubplotsChange,
  onXAxisChange,
  onYAxisFormatChange,
  onYAxisConfigChange
}) => {
  const { t } = useTranslation('training');
  const [subplotInput, setSubplotInput] = useState<string>(String(plotConfig.num_sbpl));

  const selectClasses = "w-full px-3 py-2 bg-card border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-shadow duration-150 appearance-none cursor-pointer";
  const inputClasses = "w-full px-3 py-2 bg-card border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-shadow duration-150 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground tracking-tight mb-3">
        {t('training.plotting.plotSettings')}
      </h3>
      <div className="bg-muted/50 rounded-lg border border-border p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label
              htmlFor="plot-num-subplots"
              className="text-xs font-medium text-muted-foreground block mb-1.5 uppercase tracking-wide"
            >
              {t('training.plotting.numSubplots')}
            </label>
            <input
              id="plot-num-subplots"
              type="number"
              min="1"
              max="50"
              value={subplotInput}
              onChange={(e) => {
                setSubplotInput(e.target.value);
                const parsed = parseInt(e.target.value);
                if (!isNaN(parsed) && parsed >= 1 && parsed <= 50) {
                  onNumSubplotsChange(parsed);
                }
              }}
              onBlur={() => {
                const parsed = parseInt(subplotInput);
                if (isNaN(parsed) || parsed < 1) {
                  setSubplotInput('1');
                  onNumSubplotsChange(1);
                } else if (parsed > 50) {
                  setSubplotInput('50');
                  onNumSubplotsChange(50);
                }
              }}
              className={inputClasses}
            />
          </div>

          <div>
            <label
              htmlFor="plot-x-axis"
              className="text-xs font-medium text-muted-foreground block mb-1.5 uppercase tracking-wide"
            >
              {t('training.plotting.xAxis')}
            </label>
            <select
              id="plot-x-axis"
              value={plotConfig.x_sbpl}
              onChange={(e) => onXAxisChange(e.target.value)}
              className={selectClasses}
            >
              <option value="UTC">UTC</option>
              <option value="ts">{t('training.plotting.timestep')}</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="plot-y-axis-format"
              className="text-xs font-medium text-muted-foreground block mb-1.5 uppercase tracking-wide"
            >
              {t('training.plotting.yAxisFormat')}
            </label>
            <select
              id="plot-y-axis-format"
              value={plotConfig.y_sbpl_fmt}
              onChange={(e) => onYAxisFormatChange(e.target.value)}
              className={selectClasses}
            >
              <option value="original">{t('training.plotting.original')}</option>
              <option value="skaliert">{t('training.plotting.scaled')}</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="plot-y-axis-config"
              className="text-xs font-medium text-muted-foreground block mb-1.5 uppercase tracking-wide"
            >
              {t('training.plotting.yAxisConfig')}
            </label>
            <select
              id="plot-y-axis-config"
              value={plotConfig.y_sbpl_set}
              onChange={(e) => onYAxisConfigChange(e.target.value)}
              className={selectClasses}
            >
              <option value="gemeinsame Achse">{t('training.plotting.sharedAxis')}</option>
              <option value="separate Achsen">{t('training.plotting.separateAxes')}</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
});

PlotSettingsSection.displayName = 'PlotSettingsSection';

export default PlotSettingsSection;
